import {inject} from '@angular/core';
import {Auth, signInWithCustomToken} from '@angular/fire/auth';
import {CanActivateFn} from '@angular/router';
import {ConfigService, SentryService, SessionService, User} from '@q9elements/ui-kit/common';
import {has} from 'lodash';
import {CookieService} from 'ngx-cookie-service';
import {EMPTY, from, map, switchMap, tap} from 'rxjs';

import {SharedDialogService} from '../services/shared-dialog.service';

export const AuthGuard: CanActivateFn = () => {
  const auth = inject(Auth);
  const cookieService = inject(CookieService);
  const sharedDialogService = inject(SharedDialogService);
  const sessionService = inject(SessionService);
  const sentryService = inject(SentryService);
  const url = `${ConfigService.environment.API_ENDPOINT}/admin/account`;
  const token = cookieService.get('auth-token');
  const isTokenValid = Boolean(token);

  if (!isTokenValid) {
    return redirectToMainApp();
  }

  return sessionService.startSession(url).pipe(
    switchMap(user => {
      const errorMessage = validateUserAndGetMessage(user);

      if (errorMessage) {
        sessionService.cleanupUser();

        return sharedDialogService
          .showErrorDialog(errorMessage)
          .pipe(tap(() => redirectToMainApp()));
      }
      sentryService.setContext(user as User);

      return from(signInWithCustomToken(auth, user.firebaseToken));
    }),
    map(Boolean)
  );
};

function validateUserAndGetMessage(user: User) {
  const accountIsNotActiveError = 'GENERAL.ACCOUNT_NOT_ACTIVATED';
  const userIsNotSysAdminError = 'GENERAL.NO_SYSADMIN_PERMISSION';

  if (user.state !== 'active') {
    return accountIsNotActiveError;
  }

  if (!has(user, 'sysAdminPermissions')) {
    return userIsNotSysAdminError;
  }

  return '';
}

function redirectToMainApp() {
  const url = `${ConfigService.environment.MAIN_APP_URL}/signin?redirect=${encodeURIComponent(
    window.location.href
  )}`;

  window.location.replace(String(url));

  return EMPTY;
}
